'use client'

import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useRef } from 'react'

interface FilterBarProps {
  isExpanded: boolean
  onToggle: () => void
  activeCategory: string | null
  activeSortLabel: string | null
  hasActiveFilters: boolean
  placeholder?: string
  search: string
  onSearchChange: (value: string) => void
}

export function FilterBar({
  isExpanded,
  onToggle,
  activeCategory,
  activeSortLabel,
  hasActiveFilters,
  placeholder = 'Search & filter...',
  search,
  onSearchChange,
}: FilterBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const handleSearchInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        onSearchChange(value)
      }, 200)
    },
    [onSearchChange],
  )

  // Sync input value when search is cleared externally
  useEffect(() => {
    if (inputRef.current && search === '' && inputRef.current.value !== '') {
      inputRef.current.value = ''
    }
  }, [search])

  // Auto-focus input when expanding
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isExpanded])

  return (
    <div
      className="flex items-center gap-2 px-4 py-2.5 cursor-pointer"
      onClick={(e) => {
        // Don't toggle when clicking inside the input
        if (isExpanded && (e.target as HTMLElement).tagName === 'INPUT') return
        if (!isExpanded) onToggle()
      }}
    >
      <Icon icon="search" className="w-4 h-4 text-muted shrink-0" />

      {isExpanded ? (
        /* Expanded: real input */
        <div className="flex-1 min-w-0 relative" role="search">
          <input
            ref={inputRef}
            type="text"
            defaultValue={search}
            onChange={handleSearchInput}
            placeholder={placeholder}
            className="w-full text-sm bg-transparent text-foreground placeholder:text-muted outline-none"
            aria-label={placeholder}
          />
        </div>
      ) : (
        /* Collapsed: truncated placeholder */
        <span className="text-sm text-muted flex-1 min-w-0 text-left truncate">
          {search || placeholder}
        </span>
      )}

      {/* Active filter summary pills — only when collapsed */}
      {!isExpanded && hasActiveFilters && (
        <div className="flex gap-1.5 shrink-0">
          {activeCategory && activeCategory !== 'all' && (
            <span className="bg-accent text-accent-foreground text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
              {activeCategory}
            </span>
          )}
          {activeSortLabel && (
            <span className="bg-overlay text-foreground text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
              {activeSortLabel}
            </span>
          )}
        </div>
      )}

      {/* Clear search button — only when expanded and has text */}
      {isExpanded && search && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onSearchChange('')
            if (inputRef.current) inputRef.current.value = ''
            inputRef.current?.focus()
          }}
          className="p-1 text-muted hover:text-foreground shrink-0 cursor-pointer"
          aria-label="Clear search"
        >
          <Icon icon="close" className="w-3.5 h-3.5" />
        </button>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onToggle()
        }}
        aria-expanded={isExpanded}
        aria-controls="content-filter-drawer"
        className="shrink-0 p-0.5 cursor-pointer"
        aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
      >
        <Icon
          icon={isExpanded ? 'chevron-up' : 'chevron-down'}
          className="w-3.5 h-3.5 text-muted"
        />
      </button>
    </div>
  )
}
